import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import {
    Sparkles,
    Star,
    Download,
    Zap,
    Grid3x3,
    DoorOpen,
    Shapes,
    Flower2,
    Building2,
    LayoutGrid,
    Eye,
    ShieldCheck,
    Cpu,
    FileCode2,
    ArrowRight,
    Check,
    ChevronDown,
    Quote,
    PlayCircle,
} from "lucide-react";
import { useState } from "react";

const PLAY_URL = "https://play.google.com/store/apps/details?id=com.filescad.app";
const PLAY_BADGE = "https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png";
const APP_ICON = "https://play-lh.googleusercontent.com/zqPvmjZJnHrvIqHfnpWWhkAAQF4LiYtAor5yPuolfibCLmINaN6VXEa5xDVqD9JDjaUd0YoJ3pXz2tzKGZSObw=w240-h480-rw";

const screenshots = [
    "https://play-lh.googleusercontent.com/klGf0TaoGLwAcoQukqYRDvpsLK3EpP1xQ1G30sIJuPP7SahS60TnQIcNPzEWN1Qfs2C0ICjjnsOhMTPpAFIf_g=w526-h296-rw",
    "https://play-lh.googleusercontent.com/T3Yx1UC8GX4o6OJK-r-AP2Y4y6ZBhk4z_L1kK4_Tll6k-m6cMDZS0d0czm7PrS9WSfGKtOWnZEWJgIqBNf9c=w526-h296-rw",
    "https://play-lh.googleusercontent.com/42FQRk81ZMEa68xrwZVYDqjoA_wTQUqlnuOtLa4lWcPcnEvX2J8GrZ9OTAxbfnMq-sUCsOyw51Q_hQEl0eHTN7g=w526-h296-rw",
    "https://play-lh.googleusercontent.com/J4X6epryQqK1J4oR6KTpDZw2Bpis_n_8VQFaZUf-Peegs_DqVD0sNM8HpuEZPXa1sVUsG9M1e25guSBl9Ix6Rg=w526-h296-rw",
    "https://play-lh.googleusercontent.com/BvZ0aaxcSlIGHd0GHqAP0BDIk943Q0cb6VqmvnaomKHYsPLFWHXmPIaDZHFrxT1IcdHTTURQyyT4Y4dARk6kDg=w526-h296-rw",
    "https://play-lh.googleusercontent.com/QY8479U3FLt7JYM7KOzUbReJ5Tm7sMdQLk3tmJB2frR_ryCUd8FnZkRuoOVVLoTE9c5tt-U_k9Oe25E3Wz85gw=w526-h296-rw",
    "https://play-lh.googleusercontent.com/H0axoibG3B5K4Qi_0p5i9GRIdmDCclZooxBJiQGSQiJd7FJmBlrjjOmkoS2AaKW8PJfjjyceex4nMiZut9r9=w526-h296-rw",
    "https://play-lh.googleusercontent.com/lBu6eXmmkPErCVO7BQl5i_4JFy9TgM0XUqBq-cPdb3exP6LtkZjEJWkBbFLIeGqKlCCi-ywUDWvDsnoySgKUPA=w526-h296-rw",
];

const categories = [
    { name: "Jali Patterns", icon: Grid3x3, count: "60K+", desc: "Intricate decorative screens & lattice motifs." },
    { name: "Door Designs", icon: DoorOpen, count: "25K+", desc: "Carved, modern & traditional door files." },
    { name: "Geometric", icon: Shapes, count: "20K+", desc: "Precision geometric & tessellation patterns." },
    { name: "Floral Motifs", icon: Flower2, count: "18K+", desc: "Botanical and ornamental designs." },
    { name: "Architectural", icon: Building2, count: "15K+", desc: "Facades, panels & decorative elements." },
    { name: "Mixed Library", icon: LayoutGrid, count: "12K+", desc: "Custom & contemporary CNC artwork." },
];

const features = [
    {
        icon: Zap,
        title: "Ready-to-cut files",
        desc: "Every design is optimized for direct CNC cutting. No prep, no clean-up — open, send, cut.",
    },
    {
        icon: Eye,
        title: "Full HD previews",
        desc: "Inspect every line in crystal-clear high definition before committing to a cut.",
    },
    {
        icon: FileCode2,
        title: "DXF · CDR · DWG",
        desc: "Multi-format support that works seamlessly with your existing CAM software.",
    },
    {
        icon: Cpu,
        title: "Import your own",
        desc: "Bring your own DXF and CDR files into the app to view, manage and prepare for cutting.",
    },
    {
        icon: ShieldCheck,
        title: "Secure payments",
        desc: "Razorpay-powered checkout. Your billing data is never stored on our servers.",
    },
    {
        icon: Sparkles,
        title: "Updated weekly",
        desc: "Fresh designs added by our studio team to keep your catalog moving forward.",
    },
];

const steps = [
    { n: "01", title: "Install the app", desc: "Download FilesCAD free from the Google Play Store." },
    { n: "02", title: "Browse 150K+ designs", desc: "Filter by category, style, machine type or PIN." },
    { n: "03", title: "Preview in HD", desc: "Inspect every curve before you commit." },
    { n: "04", title: "Cut with confidence", desc: "Export to your CNC and start carving — that's it." },
];

const reviews = [
    {
        name: "Rakesh M.",
        role: "Carpenter, Pune",
        body: "First app for door design files with fixed low rate. Saved me hours of redrawing every week.",
        rating: 5,
    },
    {
        name: "Anita S.",
        role: "Interior Studio Owner",
        body: "All types of designs available at low cost. The Jali collection is genuinely the best I have used.",
        rating: 5,
    },
    {
        name: "Vinod K.",
        role: "CNC Operator",
        body: "Preview quality is excellent, and the files cut clean on my router every single time.",
        rating: 4,
    },
];

const faqs = [
    {
        q: "Which file formats does FilesCAD support?",
        a: "FilesCAD provides designs in DXF, CDR and DWG — the three formats compatible with the vast majority of CNC and laser machines.",
    },
    {
        q: "Will the designs work on my CNC machine?",
        a: "Yes. Every design is built to standard CNC tolerances. If a file does not open on your machine, our 48-hour refund policy applies.",
    },
    {
        q: "Can I import my own files?",
        a: "Absolutely. FilesCAD lets you view and manage your own DXF and CDR files alongside our library.",
    },
    {
        q: "Is the app free to download?",
        a: "Yes, the app is free on the Google Play Store. Individual designs and membership plans are available inside the app.",
    },
    {
        q: "How do I get support?",
        a: "Email support@filescad.com and our team typically responds within 24 hours — much faster for membership users.",
    },
];

export default function Home() {
    const [openFaq, setOpenFaq] = useState(0);

    return (
        <div data-testid="home-page" className="min-h-screen bg-[#FAFAFA]">
            <Header />

            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
            >
                <div className="absolute inset-0 bg-blueprint pointer-events-none" />
                <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
                <div className="absolute top-40 -left-20 w-[400px] h-[400px] rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />

                <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                        <div className="lg:col-span-7">
                            <div
                                data-testid="hero-badge"
                                className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 shadow-sm"
                            >
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                                </span>
                                <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-700">
                                    Available on Google Play
                                </span>
                            </div>

                            <h1 className="mt-8 font-display font-black tracking-tighter text-slate-950 text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95]">
                                Exclusive
                                <br />
                                <span className="relative inline-block">
                                    CNC
                                    <span className="absolute -bottom-2 left-0 right-0 h-3 bg-blue-500/20 -z-0 rounded-sm" />
                                </span>{" "}
                                Designs.
                                <br />
                                <span className="text-slate-400 italic font-medium">Ready to cut.</span>
                            </h1>

                            <p className="mt-8 text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed">
                                Access <span className="font-bold text-slate-900">150K+ high-quality</span> CNC designs —
                                Jali, doors, geometric and floral motifs — in DXF, CDR and DWG. Built for designers, carpenters and CNC operators.
                            </p>

                            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                                <a
                                    href={PLAY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="hero-play-badge"
                                    className="block hover:opacity-90 transition-opacity hover:-translate-y-0.5 duration-300"
                                >
                                    <img src={PLAY_BADGE} alt="Get it on Google Play" className="h-16" />
                                </a>
                                <Link
                                    to="/download"
                                    data-testid="hero-download-pin-btn"
                                    className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white px-6 py-4 text-sm font-bold text-slate-900 hover:bg-slate-900 hover:text-white transition-all hover:-translate-y-0.5"
                                >
                                    <Download className="w-4 h-4" />
                                    Download by PIN
                                </Link>
                            </div>

                            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-500">
                                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" />150K+ designs</div>
                                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" />HD previews</div>
                                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" />48-hr refund</div>
                            </div>
                        </div>

                        {/* Phone mockup column */}
                        <div className="lg:col-span-5 relative">
                            <div className="relative mx-auto max-w-sm">
                                <div className="absolute -inset-6 bg-gradient-to-br from-blue-500/20 to-amber-400/20 rounded-[3rem] blur-2xl" />
                                <div className="relative bg-slate-950 rounded-[2.5rem] p-3 shadow-2xl shadow-slate-900/30 animate-float-slow ring-1 ring-slate-200">
                                    <div className="bg-slate-900 rounded-[2rem] overflow-hidden aspect-[9/19] relative">
                                        <img
                                            src={screenshots[0]}
                                            alt="FilesCAD app preview"
                                            className="absolute inset-0 w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-x-0 top-0 h-7 bg-slate-950 flex items-center justify-center">
                                            <div className="w-20 h-5 bg-black rounded-full" />
                                        </div>
                                    </div>
                                </div>

                                {/* Floating rating card */}
                                <div className="absolute -left-4 md:-left-12 top-12 bg-white rounded-2xl p-4 shadow-xl ring-1 ring-slate-100 rotate-[-4deg] hidden sm:block">
                                    <div className="flex items-center gap-2">
                                        <div className="flex">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                                            ))}
                                        </div>
                                        <span className="font-display font-black text-slate-900">4.9</span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">Rated on Play Store</p>
                                </div>

                                {/* Floating files card */}
                                <div className="absolute -right-2 md:-right-10 bottom-16 bg-white rounded-2xl p-4 shadow-xl ring-1 ring-slate-100 rotate-[3deg] hidden sm:block">
                                    <div className="flex items-center gap-2">
                                        <div className="w-9 h-9 rounded-xl bg-blue-50 grid place-items-center">
                                            <FileCode2 className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-500 font-mono">DXF · CDR · DWG</div>
                                            <div className="font-bold text-slate-900 text-sm">Ready to cut</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS / DARK */}
            <section className="relative bg-ink-950 text-white py-20 md:py-28 overflow-hidden">
                <div className="absolute inset-0 bg-blueprint-dark opacity-50" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
                        {[
                            { num: "150K+", label: "CNC Designs" },
                            { num: "10K+", label: "Downloads" },
                            { num: "2020", label: "Established" },
                            { num: "48h", label: "Refund window" },
                        ].map((s) => (
                            <div
                                key={s.label}
                                data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="text-left"
                            >
                                <div className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter text-white">
                                    {s.num}
                                </div>
                                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-slate-400">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section id="features" data-testid="features-section" className="py-20 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="max-w-3xl">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Why FilesCAD</div>
                        <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-950">
                            The library CNC pros<br />actually use.
                        </h2>
                        <p className="mt-6 text-lg text-slate-600 max-w-2xl">
                            We obsess over file quality so you can obsess over the cut. Each design is meticulously crafted and tested before it lands in your app.
                        </p>
                    </div>

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((f, i) => (
                            <div
                                key={f.title}
                                data-testid={`feature-${i}`}
                                className="group relative bg-white border border-slate-200 rounded-3xl p-8 hover:border-slate-900 hover:-translate-y-1 transition-all duration-300"
                            >
                                <div className="w-12 h-12 rounded-2xl bg-slate-950 grid place-items-center group-hover:bg-blue-600 transition-colors">
                                    <f.icon className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="mt-6 font-display font-bold text-xl text-slate-950">{f.title}</h3>
                                <p className="mt-3 text-slate-600 leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* APP SHOWCASE - DARK */}
            <section id="showcase" className="relative bg-ink-950 text-white py-20 md:py-32 overflow-hidden">
                <div className="absolute inset-0 bg-blueprint-dark opacity-40" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
                        <div className="lg:col-span-7">
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">App showcase</div>
                            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter">
                                Browse 150K+ designs<br />
                                <span className="text-slate-400 italic font-medium">from your pocket.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-5">
                            <p className="text-lg text-slate-300 leading-relaxed">
                                Filter by category, search by PIN, save your favorites and download instantly. Every screen is designed for fast, no-friction browsing.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Marquee of screenshots */}
                <div className="relative">
                    <div className="overflow-hidden">
                        <div className="marquee-track gap-6 px-6">
                            {[...screenshots, ...screenshots].map((src, i) => (
                                <div
                                    key={i}
                                    className="shrink-0 w-[260px] md:w-[320px] aspect-[9/16] rounded-3xl overflow-hidden ring-1 ring-white/10 bg-slate-900"
                                >
                                    <img src={src} alt={`FilesCAD app screen ${i + 1}`} className="w-full h-full object-cover" />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-ink-950 to-transparent" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-ink-950 to-transparent" />
                </div>
            </section>

            {/* CATEGORIES */}
            <section id="categories" data-testid="categories-section" className="py-20 md:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                        <div>
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Categories</div>
                            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-950">
                                A library, not a stockpile.
                            </h2>
                        </div>
                        <p className="text-slate-600 max-w-md">
                            Curated collections from intricate Jali screens to bold architectural facades — find designs that match your craft.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {categories.map((c, i) => (
                            <a
                                key={c.name}
                                href={PLAY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`category-${i}`}
                                className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 p-8 hover:border-slate-900 transition-all duration-300 hover:-translate-y-1"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="w-12 h-12 rounded-2xl bg-white grid place-items-center ring-1 ring-slate-200 group-hover:bg-blue-600 group-hover:ring-blue-600 transition-colors">
                                        <c.icon className="w-5 h-5 text-slate-900 group-hover:text-white transition-colors" />
                                    </div>
                                    <span className="font-mono text-xs text-slate-500">{c.count}</span>
                                </div>
                                <h3 className="mt-8 font-display font-bold text-2xl text-slate-950">{c.name}</h3>
                                <p className="mt-2 text-slate-600">{c.desc}</p>
                                <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                                    Browse in app <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" data-testid="how-section" className="py-20 md:py-32 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="max-w-3xl mb-16">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">How it works</div>
                        <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-950">
                            From phone to finished cut<br />in four steps.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {steps.map((s, i) => (
                            <div
                                key={s.n}
                                data-testid={`step-${i}`}
                                className="relative bg-white rounded-3xl p-8 border border-slate-200"
                            >
                                <div className="font-mono text-sm text-blue-600">{s.n}</div>
                                <h3 className="mt-4 font-display font-bold text-xl text-slate-950">{s.title}</h3>
                                <p className="mt-3 text-slate-600 leading-relaxed">{s.desc}</p>
                                {i < steps.length - 1 && (
                                    <ArrowRight className="hidden lg:block absolute top-1/2 -right-4 w-6 h-6 text-slate-300" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FILE FORMATS */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-5">
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">File formats</div>
                            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter text-slate-950">
                                Three formats.<br />Every machine.
                            </h2>
                            <p className="mt-6 text-lg text-slate-600 max-w-md">
                                Whether you run a router, plasma table or laser cutter, your workflow is covered.
                            </p>
                        </div>
                        <div className="lg:col-span-7">
                            <div className="grid grid-cols-3 gap-4">
                                {[
                                    { ext: ".DXF", label: "Drawing Exchange" },
                                    { ext: ".CDR", label: "CorelDraw" },
                                    { ext: ".DWG", label: "AutoCAD" },
                                ].map((f) => (
                                    <div
                                        key={f.ext}
                                        data-testid={`format-${f.ext.replace(".", "").toLowerCase()}`}
                                        className="relative aspect-square rounded-3xl border-2 border-dashed border-slate-300 grid place-items-center p-4 hover:border-blue-600 hover:bg-blue-50 transition-colors group"
                                    >
                                        <div className="text-center">
                                            <div className="font-mono font-bold text-2xl md:text-3xl text-slate-950">
                                                {f.ext}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-2">{f.label}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* REVIEWS */}
            <section id="reviews" data-testid="reviews-section" className="py-20 md:py-32 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="max-w-3xl mb-16">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Reviews</div>
                        <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-950">
                            From the workshop floor.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {reviews.map((r, i) => (
                            <div
                                key={r.name}
                                data-testid={`review-${i}`}
                                className="bg-white rounded-3xl p-8 border border-slate-200 relative"
                            >
                                <Quote className="absolute top-6 right-6 w-8 h-8 text-slate-100" />
                                <div className="flex gap-1">
                                    {[...Array(r.rating)].map((_, j) => (
                                        <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                                    ))}
                                </div>
                                <p className="mt-5 text-slate-700 leading-relaxed text-lg">{r.body}</p>
                                <div className="mt-6 pt-6 border-t border-slate-100">
                                    <div className="font-bold text-slate-950">{r.name}</div>
                                    <div className="text-sm text-slate-500">{r.role}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" data-testid="faq-section" className="py-20 md:py-32 bg-white">
                <div className="max-w-4xl mx-auto px-6 md:px-10">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">FAQ</div>
                    <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-950">
                        Questions, answered.
                    </h2>

                    <div className="mt-12 space-y-3">
                        {faqs.map((f, i) => {
                            const open = openFaq === i;
                            return (
                                <div
                                    key={f.q}
                                    data-testid={`faq-${i}`}
                                    className={`border rounded-2xl transition-all ${
                                        open ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white"
                                    }`}
                                >
                                    <button
                                        data-testid={`faq-toggle-${i}`}
                                        className="w-full flex items-center justify-between gap-6 p-6 text-left"
                                        onClick={() => setOpenFaq(open ? -1 : i)}
                                    >
                                        <span className="font-display font-bold text-lg md:text-xl text-slate-950">{f.q}</span>
                                        <ChevronDown
                                            className={`w-5 h-5 text-slate-500 transition-transform shrink-0 ${
                                                open ? "rotate-180" : ""
                                            }`}
                                        />
                                    </button>
                                    {open && (
                                        <div className="px-6 pb-6 text-slate-600 leading-relaxed">{f.a}</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* BIG CTA */}
            <section
                data-testid="cta-section"
                className="relative bg-ink-950 text-white py-24 md:py-32 overflow-hidden"
            >
                <div className="absolute inset-0 bg-blueprint-dark opacity-40" />
                <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-blue-600/15 blur-3xl" />

                <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 bg-white/5 backdrop-blur">
                        <PlayCircle className="w-4 h-4 text-amber-400" />
                        <span className="text-xs font-bold uppercase tracking-[0.18em]">Free on Google Play</span>
                    </div>

                    <h2 className="mt-8 font-display font-black text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95]">
                        Start cutting<br />
                        <span className="text-slate-400 italic font-medium">today.</span>
                    </h2>

                    <p className="mt-8 text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
                        Install FilesCAD and unlock 150K+ ready-to-cut designs. No subscription required to get started.
                    </p>

                    <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6">
                        <a
                            href={PLAY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="cta-play-badge"
                            className="hover:opacity-90 transition-opacity hover:-translate-y-0.5 duration-300"
                        >
                            <img src={PLAY_BADGE} alt="Get it on Google Play" className="h-16" />
                        </a>
                        <div className="flex items-center gap-4 text-slate-400">
                            <img src={APP_ICON} alt="FilesCAD app icon" className="w-14 h-14 rounded-2xl ring-1 ring-white/10" />
                            <div className="text-left">
                                <div className="font-bold text-white">FilesCAD — CNC Designs</div>
                                <div className="text-xs font-mono">com.filescad.app</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
